// ============================================
// HERO CAROUSEL — rotating model images behind hero content
// Fetches active banners from /api/hero-banners; uses tasteful
// Pexels fallbacks if none are configured.
//
// Design:
//   - Full-bleed image layer behind hero text
//   - Dark radial gradient overlay keeps text readable
//   - 5.5 sec per slide, 1.2 sec crossfade
//   - Pauses rotation on hover, resumes on leave
//   - Respects prefers-reduced-motion (no auto-rotate, no fade)
//   - Preloads next image so swap is instant
//   - Indicator dots at the bottom, clickable
//   - Optional side overlay emphasis ('left' | 'right' | 'center')
// ============================================

import React, { useState, useEffect, useRef, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || 'https://api.glitzandglambyjyoti.com';

// v36 May 10 2026 — Pexels stock photos removed (brand authenticity + 936KB
// payload). Replaced with a single Cloudinary-hosted fallback using on-the-fly
// smart-crop transformation (c_fill,w_1920,h_1080,g_auto). The hero looks
// branded the moment Jyoti uploads real banners via Admin → Hero Banners
// — see useEffect below: if /api/hero-banners returns any rows, this fallback
// is replaced wholesale.
const FALLBACK_BANNERS = [
  {
    src: 'https://res.cloudinary.com/ddwsfn1xz/image/upload/f_auto,q_auto,c_fill,w_1920,h_1080,g_auto/v1778172650/glitz-and-glam/yhaptfvt672x4lhgu0yj.jpg',
    alt: 'Glitz & Glam by Jyoti — premium artificial jewelry',
    focal: 'right',
  },
];

const SLIDE_MS    = 5500;
const FADE_MS     = 1200;

const HeroCarousel = ({ children, minHeight = '88vh', isMobile = false }) => {
  const [banners, setBanners] = useState(FALLBACK_BANNERS);
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState({ 0: false });
  const timerRef = useRef(null);

  // Detect prefers-reduced-motion once on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const listener = (e) => setReducedMotion(e.matches);
    mq.addEventListener?.('change', listener);
    return () => mq.removeEventListener?.('change', listener);
  }, []);

  // Fetch banners from backend; fall back silently to defaults on any error
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/hero-banners`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setBanners(data.map(b => ({
            src:   b.image || b.src,
            alt:   b.alt || b.title || 'Glitz & Glam featured look',
            focal: b.focal || 'right',
            ctaUrl:   b.ctaUrl,
            ctaLabel: b.ctaLabel,
          })));
        }
      })
      .catch(() => { /* keep fallbacks */ });
    return () => { cancelled = true; };
  }, []);

  // Preload the FIRST image explicitly so LCP is fast
  useEffect(() => {
    if (!banners[0]?.src) return;
    const img = new Image();
    img.src = banners[0].src;
    img.onload = () => setImagesLoaded(prev => ({ ...prev, 0: true }));
  }, [banners]);

  // Preload the NEXT image just before it shows
  useEffect(() => {
    const next = (current + 1) % banners.length;
    if (imagesLoaded[next]) return;
    if (!banners[next]?.src) return;
    const img = new Image();
    img.src = banners[next].src;
    img.onload = () => setImagesLoaded(prev => ({ ...prev, [next]: true }));
  }, [current, banners, imagesLoaded]);

  // Auto-rotate
  const advance = useCallback(() => {
    setCurrent(c => (c + 1) % banners.length);
  }, [banners.length]);

  useEffect(() => {
    if (reducedMotion || isPaused || banners.length <= 1) return;
    timerRef.current = setTimeout(advance, SLIDE_MS);
    return () => clearTimeout(timerRef.current);
  }, [current, isPaused, reducedMotion, advance, banners.length]);

  if (banners.length === 0) return <section style={{ minHeight }}>{children}</section>;

  // Build the overlay gradient based on where the model is in the frame.
  // Key design: TOP of hero is only lightly darkened (so the transparent
  // header can show the model through). MIDDLE is darker (so hero text stays
  // readable). BOTTOM fades to solid dark (seamless handoff to features strip).
  const buildOverlay = (focal) => {
    if (focal === 'left') {
      return [
        'linear-gradient(180deg, rgba(4,2,10,0.12) 0%, rgba(4,2,10,0.55) 40%, rgba(4,2,10,0.80) 75%, rgba(4,2,10,0.95) 100%)',
        'linear-gradient(90deg,  rgba(4,2,10,0.20) 0%, rgba(4,2,10,0.40) 60%, rgba(4,2,10,0.60) 100%)'
      ].join(', ');
    }
    if (focal === 'center') {
      return [
        'linear-gradient(180deg, rgba(4,2,10,0.12) 0%, rgba(4,2,10,0.50) 45%, rgba(4,2,10,0.80) 80%, rgba(4,2,10,0.95) 100%)',
        'radial-gradient(ellipse at center, transparent 0%, rgba(4,2,10,0.35) 100%)'
      ].join(', ');
    }
    // 'right' (default) — model is on the right side of the frame.
    // Vertical gradient is the primary overlay; a soft center radial adds depth.
    return [
      'linear-gradient(180deg, rgba(4,2,10,0.12) 0%, rgba(4,2,10,0.50) 45%, rgba(4,2,10,0.80) 80%, rgba(4,2,10,0.95) 100%)',
      'radial-gradient(ellipse at 50% 60%, transparent 0%, rgba(4,2,10,0.35) 70%)'
    ].join(', ');
  };

  const S = {
    wrap: {
      position: 'relative',
      minHeight: isMobile ? '70vh' : minHeight,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '40px 16px' : '60px 32px',
      textAlign: 'center',
      // Pull the hero UP into the fixed-header space so the model image extends
      // behind the transparent nav. Without this, the 120px main-content
      // padding-top leaves a dark band above the hero.
      marginTop: isMobile ? -88 : -120,
      // But pad content DOWN by the same amount so the text isn't hidden
      // under the header.
      paddingTop: isMobile ? 88 + 40 : 120 + 60,
    },
    slideLayer: {
      position: 'absolute',
      inset: 0,
      zIndex: 0,
    },
    slide: (isActive, src, focal, isLoaded) => ({
      position: 'absolute',
      inset: 0,
      backgroundImage: isLoaded ? `url("${src}")` : 'none',
      backgroundColor: '#04020a',
      backgroundSize: 'cover',
      backgroundPosition: focal === 'left' ? 'left center' : focal === 'center' ? 'center center' : 'center right',
      backgroundRepeat: 'no-repeat',
      opacity: isActive ? 1 : 0,
      transition: `opacity ${FADE_MS}ms ease-in-out`,
      willChange: 'opacity',
    }),
    overlay: (focal) => ({
      position: 'absolute',
      inset: 0,
      background: buildOverlay(focal),
      zIndex: 1,
    }),
    // Extra bottom fade for a clean handoff to the features strip
    bottomFade: {
      position: 'absolute',
      left: 0, right: 0, bottom: 0,
      height: 120,
      background: 'linear-gradient(180deg, transparent 0%, #04020a 100%)',
      zIndex: 2,
      pointerEvents: 'none',
    },
    content: {
      position: 'relative',
      zIndex: 3,
      maxWidth: 700,
      width: '100%',
      animation: 'fadeUp 0.9s ease both',
    },
    dots: {
      position: 'absolute',
      bottom: isMobile ? 20 : 32,
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      gap: 8,
      zIndex: 4,
    },
    dot: (isActive) => ({
      width: 7,
      height: 7,
      borderRadius: '50%',
      background: isActive ? '#f0d080' : 'rgba(201,168,76,0.32)',
      border: 'none',
      cursor: 'pointer',
      padding: 0,
      transition: 'all 0.3s ease',
      boxShadow: isActive ? '0 0 10px rgba(240,208,128,0.8)' : 'none',
    }),
  };

  const activeFocal = banners[current]?.focal || 'right';

  return (
    <section
      style={S.wrap}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      aria-label="Featured collection hero"
    >
      {/* Image slides */}
      <div style={S.slideLayer}>
        {banners.map((b, i) => (
          <div
            key={i}
            style={S.slide(i === current, b.src, b.focal || 'right', imagesLoaded[i])}
            role="img"
            aria-label={b.alt}
            aria-hidden={i !== current}
          />
        ))}
      </div>

      {/* Dark overlay — adapts to current slide's focal */}
      <div style={S.overlay(activeFocal)} />

      {/* Bottom fade into the next section */}
      <div style={S.bottomFade} />

      {/* Hero content (passed in from Home.js) */}
      <div style={S.content}>
        {children}
      </div>

      {/* Pagination dots — only if more than one banner */}
      {banners.length > 1 && (
        <div style={S.dots}>
          {banners.map((_, i) => (
            <button
              key={i}
              style={S.dot(i === current)}
              onClick={() => setCurrent(i)}
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === current}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default HeroCarousel;
